import axiosInstance from '../axiosConfig';
import {
  Member,
  UpdateProfileData,
  ChangePasswordData,
  FamilyMember,
  Payment,
  Invoice,
  MemberFilters,
  PaginatedResponse,
} from '../../types';

/**
 * One row in the household roster returned by GET /members/:id/household.
 * Uniform across member primary, member dependents, non-member primary, and
 * non-member-family dependents (the NM-2 layer's normalized shape).
 */
export interface HouseholdRosterEntry {
  kind: 'member' | 'member_dependent' | 'non_member' | 'non_member_dependent';
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  date_of_birth: string | null;
  is_member: boolean;
  is_self: boolean;
  member_type: string;
  is_primary: boolean;
  relationship_to_primary: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  is_locked: boolean;
  membership_status: string | null;
}

/**
 * Member Service
 * Handles all member-related API calls
 */
class MemberService {
  /**
   * Get current member profile
   * @returns Current member data
   */
  async getProfile(): Promise<Member> {
    const response = await axiosInstance.get<Member>('/members/me');
    return response.data;
  }

  /**
   * Update current member profile
   * @param data - Profile update data
   * @returns Updated member data
   */
  async updateProfile(data: UpdateProfileData): Promise<Member> {
    const response = await axiosInstance.put<Member>('/members/me', data);
    return response.data;
  }

  /**
   * Set a member's gender (self, own dependent, or staff). Drives meet brackets.
   */
  async setMemberGender(
    memberId: string,
    gender: 'female' | 'male' | 'other' | 'prefer_not_to_say',
  ): Promise<void> {
    await axiosInstance.put(`/members/${memberId}/gender`, { gender });
  }

  /**
   * Change member password
   * @param data - Password change data (current and new password)
   * @returns Success message
   */
  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const response = await axiosInstance.put<{ message: string }>(
      '/members/me/change-password',
      data
    );
    return response.data;
  }

  /**
   * Get member by ID
   * @param memberId - Member ID
   * @returns Member data
   */
  async getMemberById(memberId: string): Promise<Member> {
    const response = await axiosInstance.get<Member>(`/members/${memberId}`);
    return response.data;
  }

  /**
   * Get the household for a member: the household head's full profile + a uniform
   * roster (spouse + Member dependents + NonMemberFamily dependents). If memberId
   * is a dependent, the API follows primary_member_id and returns the primary's
   * household with focus_id echoed back. Powers the Member Detail page.
   */
  async getMemberHousehold(memberId: string): Promise<{
    success: boolean;
    data: {
      primary: Member;
      household: HouseholdRosterEntry[];
      focus_id: string;
    };
  }> {
    const response = await axiosInstance.get(`/members/${memberId}/household`);
    return response.data;
  }

  /**
   * Get all members (admin only)
   * @param filters - Optional filters and pagination
   * @returns Paginated list of members
   */
  async getAllMembers(filters?: MemberFilters): Promise<PaginatedResponse<Member>> {
    const response = await axiosInstance.get<PaginatedResponse<Member>>('/members/', {
      params: filters,
    });
    return response.data;
  }

  /**
   * Search members for booking purposes (available to all authenticated users)
   * @param query - Search query (name, email, or member number)
   * @param limit - Maximum results (default 10)
   * @returns List of matching members with limited fields
   */
  async searchMembersForBooking(query: string, limit: number = 10): Promise<{ success: boolean; data: Member[]; count: number }> {
    const response = await axiosInstance.get<{ success: boolean; data: Member[]; count: number }>('/members/search', {
      params: { query, limit },
    });
    return response.data;
  }

  /**
   * Get family members for current member
   * @returns List of family members
   */
  async getFamilyMembers(): Promise<FamilyMember[]> {
    const response = await axiosInstance.get<any>('/family-members/me');
    // The API returns { success: true, data: [...] }
    return response.data.data || response.data;
  }

  /**
   * Get family members for a specific member (staff/admin only)
   * @param memberId - Member ID
   * @returns List of family members
   */
  async getFamilyMembersByMemberId(memberId: string): Promise<FamilyMember[]> {
    const response = await axiosInstance.get<any>(`/family-members/member/${memberId}`);
    // The API returns { success: true, data: [...] }
    return response.data.data || response.data;
  }

  /**
   * Add family member to current member
   * @param data - Family member data
   * @returns Created family member
   */
  async addFamilyMember(data: Partial<FamilyMember>): Promise<FamilyMember> {
    const response = await axiosInstance.post<any>('/family-members/me', data);
    return response.data.data || response.data;
  }

  /**
   * Update family member
   * @param familyMemberId - Family member ID
   * @param data - Updated family member data
   * @returns Updated family member
   */
  async updateFamilyMember(
    familyMemberId: string,
    data: Partial<FamilyMember>
  ): Promise<FamilyMember> {
    const response = await axiosInstance.put<any>(
      `/family-members/${familyMemberId}`,
      data
    );
    return response.data.data || response.data;
  }

  /**
   * Delete family member
   * @param familyMemberId - Family member ID
   * @returns Success message
   */
  async deleteFamilyMember(familyMemberId: string): Promise<{ message: string }> {
    const response = await axiosInstance.delete<any>(
      `/family-members/${familyMemberId}`
    );
    return response.data.data || response.data;
  }

  /**
   * Get payment history for current member
   * @returns List of payments
   */
  async getPaymentHistory(): Promise<Payment[]> {
    const response = await axiosInstance.get<Payment[]>('/members/me/payments');
    return response.data;
  }

  /**
   * Get invoices for current member
   * @returns List of invoices
   */
  async getInvoices(): Promise<Invoice[]> {
    const response = await axiosInstance.get<Invoice[]>('/members/me/invoices');
    return response.data;
  }

  /**
   * Upgrade membership tier
   * @param tierId - New membership tier ID
   * @returns Updated member data
   */
  async upgradeMembershipTier(tierId: string): Promise<Member> {
    const response = await axiosInstance.post<Member>('/members/me/upgrade-tier', {
      membershipTierId: tierId,
    });
    return response.data;
  }

  /**
   * Cancel membership
   * @returns Success message
   */
  async cancelMembership(): Promise<{ message: string }> {
    const response = await axiosInstance.post<{ message: string }>('/members/me/cancel');
    return response.data;
  }

  /**
   * Reset a member's password (Admin/Staff only)
   * @param memberId - Member ID
   * @param newPassword - New password to set
   * @returns Success message
   */
  async adminResetPassword(memberId: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      `/members/${memberId}/reset-password`,
      { new_password: newPassword }
    );
    return response.data;
  }
}

const memberService = new MemberService();
export default memberService;
